import { useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ChevronDown, Play } from "lucide-react";
import { scrollToId } from "../lib/scroll";
import { useLang } from "../i18n/i18n";
import TutorialModal from "./TutorialModal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1762463176350-baed663b310c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

const line = {
  hidden: { y: "112%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.25 + i * 0.16, duration: 1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero({ settings }) {
  const ref = useRef(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const { t } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const price = settings ? settings.price_per_gram_24k : null;

  return (
    <section ref={ref} className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0">
        <motion.img
          src={HERO_IMG}
          alt="Lingots d'or"
          initial={{ scale: 1.18 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/35 to-ink" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-transparent to-ink/40" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8 pb-24 pt-40">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="flex items-center gap-4 mb-7"
        >
          <span className="h-px w-12 bg-gold/70" />
          <span className="text-[11px] sm:text-xs tracking-[0.4em] uppercase text-goldlight/90">{t("hero.eyebrow")}</span>
        </motion.div>

        <h1 className="font-display font-light text-cream text-[13vw] sm:text-7xl lg:text-8xl leading-[0.98] tracking-tight">
          <span className="block overflow-hidden pb-1">
            <motion.span custom={0} variants={line} initial="hidden" animate="show" className="block">
              {t("hero.t1")}
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span custom={1} variants={line} initial="hidden" animate="show" className="block">
              {t("hero.t2a")} <em className="gold-text not-italic italic font-medium">{t("hero.t2b")}</em>
              {t("hero.t2c")}
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-cream/75"
        >
          {t("hero.sub")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollToId("calculateur")}
            data-testid="hero-cta-estimate"
            className="rounded-full bg-gold px-8 py-4 text-sm font-semibold tracking-wide text-ink transition-all hover:bg-goldlight hover:shadow-[0_0_36px_rgba(212,175,55,0.4)]"
          >
            {t("hero.cta1")}
          </button>
          <button
            onClick={() => scrollToId("download")}
            data-testid="hero-cta-download"
            className="rounded-full border border-cream/25 px-8 py-4 text-sm font-semibold tracking-wide text-cream transition-all hover:border-gold hover:text-goldlight"
          >
            {t("hero.cta2")}
          </button>
          {price && (
            <span className="inline-flex items-center gap-2 rounded-full border border-gold/25 bg-ink/40 backdrop-blur px-4 py-2 text-xs text-goldlight/90 tabular">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-gold" />
              </span>
              {t("calc.gold")} 24 K — {price.toLocaleString("fr-CA", { minimumFractionDigits: 2 })} $/g
            </span>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8 }}
          className="mt-5"
        >
          <button
            onClick={() => setShowTutorial(true)}
            data-testid="hero-tutorial-open"
            className="group inline-flex items-center gap-3 text-base font-semibold text-cream transition-colors hover:text-goldlight"
          >
            <span className="relative grid h-12 w-12 place-items-center rounded-full bg-gold shadow-[0_0_28px_rgba(212,175,55,0.5)] transition-transform duration-300 group-hover:scale-105">
              <span className="absolute inset-0 rounded-full bg-gold/50 animate-ping opacity-40" />
              <Play size={18} fill="currentColor" className="relative ml-0.5 text-ink" />
            </span>
            {t("hero.tutorial")}
          </button>
        </motion.div>
      </motion.div>

      <AnimatePresence>
        {showTutorial && <TutorialModal onClose={() => setShowTutorial(false)} />}
      </AnimatePresence>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
        className="absolute bottom-6 right-8 text-gold/60 hidden sm:block"
      >
        <ChevronDown size={26} />
      </motion.div>
    </section>
  );
}
